import { useLocation, useNavigate } from "react-router";
import { MakeRequired } from "@swo/shared";
import { useCallback, useEffect } from "react";
import { useEqualState } from "../../../hooks";

export type PaymentStepCtx = {
  orderId: string;
  paymentId?: string | null | undefined;
};
export type PaymentStepCtxReq = MakeRequired<PaymentStepCtx, "paymentId" | "orderId">;

export type PaymentStep =
  | {
      key: string;
      tag: "result";
      context: PaymentStepCtxReq;
    }
  | {
      key: string;
      tag: "order";
      context: { orderId: string; paymentId?: undefined };
    }
  | {
      key: string;
      tag: "pay-again";
      context: { orderId: string; paymentId?: undefined };
    }
  | {
      key: string;
      tag: "finalize";
      context: PaymentStepCtxReq;
      redirectResult: string;
    };

export type UsePaymentStepParams = {
  paymentId: string | undefined | null;
  orderId: string | undefined | null;
  redirectResult: string | undefined | null;
  payAgain: string | undefined | null;
};

export function usePaymentStep(): readonly [PaymentStep | null, (ctx: UsePaymentStepParams) => void] {
  // These parameters are used by GTM for tracking purposes
  // If changes are needed, please contact with a person responsible for GTM integration
  // to coordinate migration
  const location = useLocation /*<{ data?: unknown }>*/();
  const navigate = useNavigate();
  const urlParams = new URLSearchParams(location.search);

  const [paymentStep, setPaymentStep] = useEqualState<PaymentStep | null>(getPaymentState(urlParams));

  useEffect(() => {
    setPaymentStep(getPaymentState(new URLSearchParams(location.search)));
  }, [location.key]);

  function getPaymentState(params: URLSearchParams): PaymentStep | null {
    // Just for context
    const orderId = params.get("orderId");
    const paymentId = params.get("paymentId");

    if (!orderId) {
      return null;
    }

    const redirectResult = params.get("redirectResult");
    const payAgain = params.get("pay-again");

    return getStateFromParams({
      orderId,
      paymentId,
      redirectResult,
      payAgain,
    });
  }

  function getStateFromParams(params: UsePaymentStepParams) {
    const { redirectResult, paymentId, orderId, payAgain } = params;
    const key = Date.now().toString();

    if (redirectResult && paymentId && orderId) {
      return {
        key: key,
        tag: "finalize" as const,
        redirectResult,
        context: {
          paymentId: paymentId,
          orderId: orderId,
        },
      };
    }

    if (payAgain && orderId) {
      return {
        key: key,
        tag: "pay-again" as const,
        context: {
          orderId: orderId,
        },
      };
    }

    if (paymentId && orderId) {
      return {
        key: key,
        tag: "result" as const,
        context: {
          paymentId: paymentId,
          orderId: orderId,
        },
      };
    }

    if (orderId) {
      return {
        key: key,
        tag: "order" as const,
        context: {
          orderId: orderId,
        },
      };
    }

    return null;
  }

  const updatePaymentStep = useCallback(
    (ctx: UsePaymentStepParams) => {
      const state = getStateFromParams(ctx);

      if (!state) {
        console.log("No state");
        return;
      }

      const { orderId, paymentId } = state.context;

      let ret: Record<string, string> = {};
      if (orderId) {
        ret.orderId = orderId;
      }
      if (paymentId) {
        ret.paymentId = paymentId;
      }

      const params = new URLSearchParams(ret).toString();

      return navigate({
        search: `?${params}`,
      });
    },
    [navigate],
  );

  return [paymentStep, updatePaymentStep] as const;
}
